import React, { useEffect, useState, useRef, Fragment, lazy, memo, createElement, Component } from 'react';
import { Shield, Zap, Check, ArrowRight, AlertTriangle, Download, ChevronDown, ChevronUp, Clock, CreditCard, Package, Star, X, Minus, MessageSquare, AlertCircle, Brain, DollarSign, BookX, Video, HardHat, Flame, Building2, CircuitBoard, Cpu, Settings, Target, TrendingUp, Award, GitBranch, Lightbulb, Headphones, ChevronLeft, ChevronRight, Eye, Ticket } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { useMetaPixel } from '../hooks/useMetaPixel';
import { usePurchaseNotifications } from '../hooks/usePurchaseNotifications';
import { PurchaseNotification } from '../components/PurchaseNotification';
// FAQ Component
function FAQItem({
  question,
  answer
}: {
  question: string;
  answer: string;
}) {
  const [isOpen, setIsOpen] = useState(false);
  return <div className="border border-gray-200 rounded-lg mb-3 overflow-hidden bg-white" data-id="element-32">
      <button onClick={() => setIsOpen(!isOpen)} className="w-full px-6 py-4 text-left flex justify-between items-center hover:bg-gray-50 transition-colors" data-id="element-33">
        <span className="font-medium text-gray-900 pr-4" data-id="element-34">{question}</span>
        {isOpen ? <ChevronUp className="w-5 h-5 text-blue-600 flex-shrink-0" data-id="element-35" /> : <ChevronDown className="w-5 h-5 text-gray-400 flex-shrink-0" data-id="element-36" />}
      </button>
      {isOpen && <div className="px-6 py-4 bg-gray-50 border-t border-gray-200" data-id="element-37">
          <p className="text-gray-700 leading-relaxed" data-id="element-38">{answer}</p>
        </div>}
    </div>;
}
// Image Carousel Component
function ImageCarousel({
  images
}: {
  images: Array<{
    image: string;
    caption: string;
  }>;
}) {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [touchStart, setTouchStart] = useState(0);
  const [touchEnd, setTouchEnd] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  useEffect(() => {
    if (isPaused) return;
    const interval = setInterval(() => {
      setCurrentIndex(prev => prev === images.length - 1 ? 0 : prev + 1);
    }, 4000);
    return () => clearInterval(interval);
  }, [images.length, isPaused]);
  const handleTouchStart = (e: React.TouchEvent | React.MouseEvent) => {
    setIsPaused(true);
    setTimeout(() => setIsPaused(false), 5000);
    if ('touches' in e) {
      setTouchStart(e.targetTouches[0].clientX);
    } else {
      setTouchStart((e as React.MouseEvent).clientX);
    }
  };
  const handleTouchMove = (e: React.TouchEvent | React.MouseEvent) => {
    if ('touches' in e) {
      setTouchEnd(e.targetTouches[0].clientX);
    } else {
      setTouchEnd((e as React.MouseEvent).clientX);
    }
  };
  const handleTouchEnd = () => {
    if (!touchStart || !touchEnd) return;
    const distance = touchStart - touchEnd;
    if (distance > 50) setCurrentIndex(prev => prev === images.length - 1 ? 0 : prev + 1);
    if (distance < -50) setCurrentIndex(prev => prev === 0 ? images.length - 1 : prev - 1);
    setTouchEnd(0);
    setTouchStart(0);
  };
  return <div className="relative max-w-2xl mx-auto touch-pan-y" onTouchStart={handleTouchStart} onTouchMove={handleTouchMove} onTouchEnd={handleTouchEnd} onMouseDown={handleTouchStart} onMouseMove={handleTouchMove} onMouseUp={handleTouchEnd} onMouseLeave={handleTouchEnd} data-id="element-39">
      <div className="relative bg-gray-50 border-2 border-gray-200 rounded-xl overflow-hidden shadow-lg select-none cursor-grab active:cursor-grabbing" data-id="element-40">
        <div className="aspect-[3/4] relative" data-id="element-41">
          <img src={images[currentIndex].image} alt={images[currentIndex].caption} className="w-full h-full object-cover transition-opacity duration-500 pointer-events-none" loading="lazy" decoding="async" width={640} height={853} data-id="element-42" />
        </div>
        <div className="p-4 bg-white border-t border-gray-100" data-id="element-43">
          <p className="text-sm font-medium text-gray-700 text-center" data-id="element-44">
            {images[currentIndex].caption}
          </p>
        </div>
      </div>
      <div className="flex justify-center gap-2 mt-6" data-id="element-45">
        {images.map((_, index) => <button key={index} onClick={() => {
        setCurrentIndex(index);
        setIsPaused(true);
        setTimeout(() => setIsPaused(false), 5000);
      }} className={`h-2 rounded-full transition-all duration-300 ${index === currentIndex ? 'bg-blue-600 w-8' : 'bg-gray-300 w-2'}`} aria-label={`Ir a imagen ${index + 1}`} data-id="element-46" />)}
      </div>
    </div>;
}
// Dynamic Stats Hook
function useDynamicStats() {
  const [viewers, setViewers] = useState(47);
  const [urgencySpots, setUrgencySpots] = useState(15);
  const [upsellSpots, setUpsellSpots] = useState(() => {
    const saved = localStorage.getItem('upsellSpots');
    return saved ? Math.max(2, parseInt(saved, 10)) : 8;
  });
  const [mainBuyers, setMainBuyers] = useState(() => {
    const saved = localStorage.getItem('mainBuyers');
    return saved ? parseInt(saved, 10) : 3251;
  });
  const [mainSpots, setMainSpots] = useState(15);
  const [comboBuyers, setComboBuyers] = useState(() => {
    const saved = localStorage.getItem('comboBuyers');
    return saved ? parseInt(saved, 10) : 5162;
  });
  const [comboSpots, setComboSpots] = useState(7);
  // Persist buyers and upsellSpots to localStorage
  useEffect(() => {
    localStorage.setItem('mainBuyers', mainBuyers.toString());
  }, [mainBuyers]);
  useEffect(() => {
    localStorage.setItem('comboBuyers', comboBuyers.toString());
  }, [comboBuyers]);
  useEffect(() => {
    localStorage.setItem('upsellSpots', upsellSpots.toString());
  }, [upsellSpots]);
  useEffect(() => {
    // Viewers fluctuation (40-63 range)
    const viewerInterval = setInterval(() => {
      setViewers(prev => {
        const change = Math.floor(Math.random() * 5) - 2;
        return Math.max(40, Math.min(63, prev + change));
      });
    }, 3000 + Math.random() * 4000);
    // Profesionales (social proof): increase 1-3 every 10s
    const mainBuyersInterval = setInterval(() => {
      const increment = Math.floor(Math.random() * 3) + 1;
      setMainBuyers(prev => prev + increment);
    }, 10000);
    const comboBuyersInterval = setInterval(() => {
      const increment = Math.floor(Math.random() * 3) + 1;
      setComboBuyers(prev => prev + increment);
    }, 10000);
    // Card 1 cupos (scarcity): decrease 1 every 15-40s, min 2
    const urgencySpotsInterval = setInterval(() => {
      setUrgencySpots(prev => Math.max(2, prev - 1));
    }, 15000 + Math.random() * 25000);
    // Upsell cupos (scarcity): decrease 1 every 120-180s, min 2
    const upsellSpotsInterval = setInterval(() => {
      setUpsellSpots(prev => Math.max(2, prev - 1));
    }, 120000 + Math.random() * 60000);
    return () => {
      clearInterval(viewerInterval);
      clearInterval(mainBuyersInterval);
      clearInterval(comboBuyersInterval);
      clearInterval(urgencySpotsInterval);
      clearInterval(upsellSpotsInterval);
    };
  }, []);
  return {
    viewers,
    urgencySpots,
    upsellSpots,
    mainBuyers,
    mainSpots,
    comboBuyers,
    comboSpots
  };
}
// Current Date Hook
function useCurrentDate() {
  const [dateString, setDateString] = useState('');
  const [shortDate, setShortDate] = useState('');
  const [weekdayDate, setWeekdayDate] = useState('');
  useEffect(() => {
    const now = new Date();
    const options: Intl.DateTimeFormatOptions = {
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    };
    setDateString(now.toLocaleDateString('es-AR', options));
    const dd = String(now.getDate()).padStart(2, '0');
    const mm = String(now.getMonth() + 1).padStart(2, '0');
    const yyyy = now.getFullYear();
    setShortDate(`${dd}/${mm}/${yyyy}`);
    // Weekday + day + month (e.g. "Lunes 16 de Marzo")
    const weekdays = ['Domingo', 'Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes', 'Sábado'];
    const months = ['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio', 'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'];
    setWeekdayDate(`${weekdays[now.getDay()]} ${now.getDate()} de ${months[now.getMonth()]}`);
  }, []);
  return {
    dateString,
    shortDate,
    weekdayDate
  };
}
// Review Form Component
function ReviewForm({
  onSubmitReview
}: {
  onSubmitReview: (review: {
    name: string;
    text: string;
    stars: number;
  }) => void;
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [name, setName] = useState('');
  const [comment, setComment] = useState('');
  const [rating, setRating] = useState(0);
  const [hoverRating, setHoverRating] = useState(0);
  const [showSuccess, setShowSuccess] = useState(false);
  const [error, setError] = useState('');
  const handleSubmit = () => {
    if (!name.trim()) {
      setError('Por favor ingresá tu nombre');
      return;
    }
    if (rating === 0) {
      setError('Por favor seleccioná una calificación');
      return;
    }
    if (!comment.trim()) {
      setError('Por favor escribí un comentario');
      return;
    }
    setError('');
    setShowSuccess(true);
    onSubmitReview({
      name: name.trim(),
      text: comment.trim(),
      stars: rating
    });
    setName('');
    setComment('');
    setRating(0);
    setTimeout(() => {
      setShowSuccess(false);
      setIsOpen(false);
    }, 3000);
  };
  if (!isOpen) {
    return <div className="text-center mt-8" data-id="element-47">
        <button onClick={() => setIsOpen(true)} className="text-gray-500 hover:text-blue-600 text-sm font-medium underline transition-colors" data-id="element-48">
          ¿Compraste? Dejá tu reseña
        </button>
      </div>;
  }
  return <div className="bg-white border border-gray-200 rounded-xl p-6 mt-8 max-w-2xl mx-auto shadow-sm relative animate-fade-in-up" data-id="element-49">
      {showSuccess && <div className="absolute top-4 right-4 bg-green-600 text-white px-4 py-2 rounded-lg shadow-lg text-sm font-medium z-10" data-id="element-50">
          <p data-id="element-51">¡Gracias por tu reseña!</p>
        </div>}
      <div className="flex justify-between items-center mb-4" data-id="element-52">
        <h4 className="font-bold text-gray-900 flex items-center gap-2" data-id="element-53">
          <MessageSquare className="w-5 h-5 text-blue-600" data-id="element-54" />
          Dejá tu reseña
        </h4>
        <button onClick={() => setIsOpen(false)} className="text-gray-400 hover:text-gray-600" data-id="element-55">
          <X className="w-5 h-5" data-id="element-56" />
        </button>
      </div>
      <div className="space-y-4" data-id="element-57">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-58">
          <input type="text" placeholder="Tu nombre" value={name} onChange={e => setName(e.target.value)} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all" data-id="element-59" />
          <div className="flex items-center gap-1 px-4 py-2 border border-gray-300 rounded-lg bg-white" data-id="element-60">
            {[1, 2, 3, 4, 5].map(s => <Star key={s} className={`w-5 h-5 cursor-pointer transition-colors ${s <= (hoverRating || rating) ? 'fill-yellow-400 text-yellow-400' : 'text-gray-300'}`} onMouseEnter={() => setHoverRating(s)} onMouseLeave={() => setHoverRating(0)} onClick={() => setRating(s)} data-id="element-61" />)}
          </div>
        </div>
        <textarea placeholder="Contanos tu experiencia..." rows={3} value={comment} onChange={e => setComment(e.target.value)} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all resize-none" data-id="element-62" />
        {error && <p className="text-red-500 text-sm" data-id="element-63">{error}</p>}
        <button onClick={handleSubmit} className="bg-blue-600 hover:bg-blue-700 text-white font-bold py-2 px-6 rounded-lg transition-colors text-sm" data-id="element-64">
          Enviar reseña
        </button>
      </div>
    </div>;
}
// Offer Selector Component
function OfferSelector({
  selected,
  onChange
}: {
  selected: 'solo' | 'combo';
  onChange: (val: 'solo' | 'combo') => void;
}) {
  return <div className="flex flex-col items-center mb-12 w-full max-w-4xl mx-auto px-2" data-id="element-65">
      <div className="flex flex-col md:flex-row w-full gap-4" data-id="element-66">
        {/* Solo Maestro Button */}
        <button onClick={() => onChange('solo')} className={`relative flex-1 px-6 py-6 md:py-8 rounded-xl font-bold text-lg md:text-xl transition-all duration-300 border-2 uppercase tracking-tight ${selected === 'solo' ? 'bg-[#0a1a3a] border-blue-500 text-white animate-neon-blue z-10' : 'bg-[#0f0f0f] border-gray-800 text-gray-500 hover:text-gray-400 hover:border-gray-700 opacity-60'}`} data-id="element-67">
          {selected === 'solo' && <div className="absolute top-3 left-3 w-2.5 h-2.5 bg-blue-500 rounded-full shadow-[0_0_8px_rgba(59,130,246,0.8)]" data-id="element-68" />}
          SOLO MAESTRO ELÉCTRICO
        </button>

        {/* Combo Button */}
        <button onClick={() => onChange('combo')} className={`relative flex-1 px-6 py-6 md:py-8 rounded-xl font-bold text-lg md:text-xl transition-all duration-300 border-2 uppercase tracking-tight flex flex-col items-center justify-center gap-1 ${selected === 'combo' ? 'bg-black border-[#FFFF00] text-white animate-neon-yellow z-10' : 'bg-[#0a0a0a] border-gray-800 text-gray-500 hover:text-gray-400 hover:border-gray-700 opacity-60'}`} data-id="element-69">
          {selected === 'combo' && <div className="absolute top-3 left-3 w-2.5 h-2.5 bg-[#FFFF00] rounded-full shadow-[0_0_8px_rgba(255,255,0,0.8)]" data-id="element-70" />}
          {/* Embedded Badge */}
          <div className="absolute -top-3 right-4 bg-[#FFFF00] text-black text-[10px] font-black px-3 py-1 rounded-full shadow-[0_0_15px_rgba(255,255,0,0.4)] z-10 whitespace-nowrap uppercase tracking-widest animate-badge-glow" data-id="element-71">
            MÁS ELEGIDO
          </div>

          {/* Engraved Seal Icon */}
          <div className="absolute bottom-3 right-3 opacity-20 group-hover:opacity-40 transition-opacity" data-id="element-72">
            <Award className="w-8 h-8 md:w-10 md:h-10 text-[#FFFF00]" strokeWidth={1} data-id="element-73" />
          </div>

          <span data-id="element-74">COMBO MAESTRO ELÉCTRICO + PLC MASTER</span>
        </button>
      </div>
    </div>;
}
export function LandingPage() {
  const {
    trackEvent
  } = useMetaPixel();
  const {
    notification,
    isVisible
  } = usePurchaseNotifications();
  // Preload hero image for LCP optimization
  useEffect(() => {
    const link = document.createElement('link');
    link.rel = 'preload';
    link.as = 'image';
    link.href = 'https://cdn.magicpatterns.com/uploads/fMZ5Fq5SNbxYwzfww2mdPS/Diseno_sin_titulo_(1).webp';
    link.fetchPriority = 'high';
    document.head.appendChild(link);
    return () => {
      document.head.removeChild(link);
    };
  }, []);
  const {
    viewers,
    urgencySpots,
    upsellSpots,
    mainBuyers,
    mainSpots,
    comboBuyers,
    comboSpots
  } = useDynamicStats();
  const {
    dateString: currentDate,
    shortDate,
    weekdayDate
  } = useCurrentDate();
  const [headerVisible, setHeaderVisible] = useState(false);
  const [selectedOffer, setSelectedOffer] = useState<'solo' | 'combo'>('combo');
  const [fearsVisible, setFearsVisible] = useState(false);
  const [problemVisible, setProblemVisible] = useState(false);
  const [problemRealVisible, setProblemRealVisible] = useState(false);
  const [decisionVisible, setDecisionVisible] = useState(false);
  const [benefitsVisible, setBenefitsVisible] = useState(false);
  const [postHeroVisible, setPostHeroVisible] = useState(false);
  const [showStickyBar, setShowStickyBar] = useState(false);
  const [scrollDirection, setScrollDirection] = useState<'up' | 'down'>('up');
  const [isManuallyExpanded, setIsManuallyExpanded] = useState(false);
  const [visibleTestimonials, setVisibleTestimonials] = useState(5);
  const [localReview, setLocalReview] = useState<{
    name: string;
    text: string;
    stars: number;
  } | null>(null);
  const [bonusIndex, setBonusIndex] = useState(0);
  const bonusScrollRef = useRef<HTMLDivElement>(null);
  const marqueeResumeTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [marqueePaused, setMarqueePaused] = useState(false);
  const marqueeContainerRef = useRef<HTMLDivElement>(null);
  const dragStartX = useRef(0);
  const dragScrollLeft = useRef(0);
  const isDragging = useRef(false);
  // Marquee pause/resume handlers
  const pauseMarquee = () => {
    setMarqueePaused(true);
    if (marqueeResumeTimer.current) clearTimeout(marqueeResumeTimer.current);
    marqueeResumeTimer.current = setTimeout(() => {
      setMarqueePaused(false);
    }, 2000);
  };
  // Drag handlers for manual swipe
  const handleDragStart = (clientX: number) => {
    const container = marqueeContainerRef.current;
    if (!container) return;
    isDragging.current = true;
    dragStartX.current = clientX;
    dragScrollLeft.current = container.scrollLeft;
    setMarqueePaused(true);
    if (marqueeResumeTimer.current) clearTimeout(marqueeResumeTimer.current);
  };
  const handleDragMove = (clientX: number) => {
    if (!isDragging.current) return;
    const container = marqueeContainerRef.current;
    if (!container) return;
    const dx = clientX - dragStartX.current;
    container.scrollLeft = dragScrollLeft.current - dx;
  };
  const handleDragEnd = () => {
    isDragging.current = false;
    if (marqueeResumeTimer.current) clearTimeout(marqueeResumeTimer.current);
    marqueeResumeTimer.current = setTimeout(() => {
      setMarqueePaused(false);
    }, 2000);
  };
  // Argentine chat reviews pool (30+)
  const allChatReviews = [{
    name: 'Javier M.',
    msg: 'Che, posta que me cambió la forma de laburar. Antes conectaba de memoria y ahora entiendo qué hace cada cable 💪',
    time: '14:32',
    verified: true
  }, {
    name: 'Gustavo R.',
    msg: 'Re sirve loco. Directo al grano, nada de vueltas teóricas al pedo. Los diagramas están claritos',
    time: '09:15',
    verified: true
  }, {
    name: 'Pablo S.',
    msg: 'Me salvó las papas con un cliente. Tenía que automatizar un tanque y no le encontraba la vuelta. Con esto lo saqué de taquito 🔧',
    time: '18:47',
    verified: true
  }, {
    name: 'Andrés C.',
    msg: 'Vale cada peso hermano. Solo con la planilla de presupuesto ya se paga solo. La uso para todos los laburos',
    time: '11:03',
    verified: true
  }, {
    name: 'Matías B.',
    msg: 'Laburo en mantenimiento y esto me ordenó la cabeza mal. Antes adivinaba, ahora sé lo que hago ⚡',
    time: '16:22',
    verified: true
  }, {
    name: 'Ricardo A.',
    msg: 'Soy técnico hace 10 pirulos y siempre se aprende algo nuevo. El protocolo de verificación es un caño, lo uso siempre',
    time: '20:11',
    verified: true
  }, {
    name: 'Marcelo D.',
    msg: 'Al principio me costó un toque porque venía de cero, pero leyéndolo tranqui se entiende todo. Me salvó con un tablero de bombas',
    time: '08:45',
    verified: false
  }, {
    name: 'Fernando T.',
    msg: 'Es técnico pero se deja leer. No es esos libros aburridos de ingeniería que te dormís. Es para el que está en la obra posta',
    time: '13:20',
    verified: true
  }, {
    name: 'Jorge L.',
    msg: 'Lo imprimí y lo llevo en la caja de herramientas. Cuando tengo una duda lo miro ahí nomás. Re práctico',
    time: '17:55',
    verified: true
  }, {
    name: 'Sergio F.',
    msg: 'Está muy completo che. Estaría bueno que tenga videos, pero las explicaciones escritas son un golazo igual',
    time: '10:30',
    verified: true
  }, {
    name: 'Diego P.',
    msg: 'Ni hablar, lo mejor que compré en años. Ahora los tableros no me asustan más jaja',
    time: '15:12',
    verified: true
  }, {
    name: 'Ramiro V.',
    msg: 'Capo el que armó esto. Se nota que sabe de campo, no es un teórico de escritorio. Re recomendado 👌',
    time: '19:40',
    verified: true
  }, {
    name: 'Hernán G.',
    msg: 'Arranqué a tomar laburos más grandes gracias a esto. Antes les tenía miedo a los tableros industriales',
    time: '07:55',
    verified: true
  }, {
    name: 'Leandro M.',
    msg: 'Posta que vale la pena. Mi viejo es electricista y se lo pasé, quedó re manija también',
    time: '12:18',
    verified: false
  }, {
    name: 'Cristian R.',
    msg: 'Lo que más me sirvió fue entender la lógica de los contactores. Antes los conectaba y rezaba jaja',
    time: '21:05',
    verified: true
  }, {
    name: 'Facundo L.',
    msg: 'Che re completo. Los bonos solos ya valen más que lo que pagué. Un golazo la planilla de presupuesto',
    time: '14:50',
    verified: true
  }, {
    name: 'Nicolás S.',
    msg: 'Soy de Córdoba y acá hay mucho laburo industrial. Con esto me animé a meterme en automatización. Gracias crack 🙌',
    time: '16:33',
    verified: true
  }, {
    name: 'Martín H.',
    msg: 'Lo arranqué a leer un domingo y no lo solté. Está explicado como si te lo contara un compañero de laburo',
    time: '11:47',
    verified: true
  }, {
    name: 'Walter B.',
    msg: 'Tengo 15 años en el rubro y hay cosas que no sabía. El tema de protecciones me voló la cabeza',
    time: '09:22',
    verified: true
  }, {
    name: 'Carlos A.',
    msg: 'Muy bueno loco. Lo único que le falta son más ejemplos de variadores, pero para mando es lo mejor que vi',
    time: '18:15',
    verified: false
  }, {
    name: 'Ezequiel D.',
    msg: 'Me lo recomendó un colega y la verdad que tenía razón. Re sirve para el día a día en obra',
    time: '13:08',
    verified: true
  }, {
    name: 'Damián F.',
    msg: 'Che, el bono de paneles solares es tremendo. Estoy pensando en meterme en eso también. Todo suma 🔋',
    time: '20:30',
    verified: true
  }, {
    name: 'Lucas T.',
    msg: 'Posta que cambió mi forma de presupuestar. Antes tiraba números al voleo, ahora tengo todo ordenado',
    time: '15:42',
    verified: true
  }, {
    name: 'Alejandro N.',
    msg: 'Lo mejor es que lo podés consultar en la obra. Nada de andar buscando en YouTube a las apuradas',
    time: '08:10',
    verified: true
  }, {
    name: 'Tomás R.',
    msg: 'Soy de Mendoza, laburo en bodegas. Los circuitos de mando de las bombas ahora los entiendo de verdad',
    time: '17:25',
    verified: true
  }, {
    name: 'Ignacio C.',
    msg: 'Un amigo me dijo "compralo que no te vas a arrepentir" y tenía razón. Muy claro todo',
    time: '12:55',
    verified: false
  }, {
    name: 'Santiago M.',
    msg: 'Re piola el material. Se nota que está hecho por alguien que labura en serio, no un profesor de facultad',
    time: '19:18',
    verified: true
  }, {
    name: 'Agustín V.',
    msg: 'Che lo del protocolo de verificación es genial. Ahora antes de dar tensión reviso todo como corresponde',
    time: '10:05',
    verified: true
  }, {
    name: 'Maximiliano P.',
    msg: 'Increíble la relación precio-calidad. Con lo que sale una herramienta tenés conocimiento para toda la vida',
    time: '14:40',
    verified: true
  }, {
    name: 'Gonzalo E.',
    msg: 'Lo compré medio desconfiado y me sorprendió. Está muy bien armado, se nota el laburo que le metieron 💯',
    time: '16:58',
    verified: true
  }];
  const testimonialData = [{
    name: 'Lucas G.',
    specialty: 'Técnico Electromecánico',
    study: 'Escuela Técnica Raggio',
    comment: 'La claridad de los manuales es impresionante. Poder tener toda la lógica de PLC y mando en un solo lugar me ahorra horas de búsqueda y errores en obra.',
    image: 'https://cdn.magicpatterns.com/uploads/9REeJugtPE4hwPnc79PgSY/Una_foto_fotorrealista_202604171241.webp'
  }, {
    name: 'Juan P.',
    specialty: 'Instalador Eléctrico',
    study: 'Escuela Técnica N°1',
    comment: 'Lo que más me sirvió fue la parte de lógica de mando. Dejé de copiar esquemas y empecé a entender por qué se conecta cada cable.',
    image: 'https://cdn.magicpatterns.com/uploads/7CHbsG1wjqAEbXPkMdWWtf/Hyper-realistic,_intimate_phone_202604151004.webp'
  }, {
    name: 'Sergio L.',
    specialty: 'Electricista Matriculado',
    study: 'COPIME',
    comment: 'La inversión se recupera en el primer trabajo que hacés con seguridad. Mis clientes notan la diferencia en mi criterio técnico.',
    image: 'https://cdn.magicpatterns.com/uploads/urm1EM6Ho19epW6AzKFAHc/Photorealistic,_intimate_medium_202604151012.webp'
  }, {
    name: 'Roberto G.',
    specialty: 'Electricista Industrial',
    study: 'UTN (Univ. Tecnológica Nacional)',
    comment: 'El manual de control eléctrico me cambió la forma de encarar los tableros. Antes dudaba mucho, ahora entiendo la lógica de punta a punta.',
    image: 'https://cdn.magicpatterns.com/uploads/ocqY51CbjtDLpjCtARHT4W/Hyper-realistic,_intimate_phone_202604151010.webp'
  }, {
    name: 'Miguel A.',
    specialty: 'Técnico en Automatización',
    study: 'Centro de Formación Profesional',
    comment: 'Muy recomendado para los que recién arrancan y para los que ya tienen años. Siempre se aprende algo nuevo con estos manuales.',
    image: 'https://cdn.magicpatterns.com/uploads/uR18KAMY2DX3wu2XqJbQph/Hyper-realistic,_candid_selfie_202604150954.webp'
  }, {
    name: 'Carlos M.',
    specialty: 'Mantenimiento de Plantas',
    study: 'Instituto Técnico Superior',
    comment: 'Excelente material. Los diagramas son súper claros y me sirvieron para resolver una falla en un motor que me tenía loco.',
    image: 'https://cdn.magicpatterns.com/uploads/dDcLT75SACqVQUA8UnAgUK/Hyper-realistic,_documentary-style_photograph._202604151012.webp'
  }];
  // Scroll reveal for sections
  useEffect(() => {
    const revealObserver = new IntersectionObserver(entries => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.classList.add('animate-reveal');
          revealObserver.unobserve(entry.target);
        }
      });
    }, {
      threshold: 0.1
    });
    document.querySelectorAll('[data-reveal]').forEach(el => {
      revealObserver.observe(el);
    });
    // Observer for sticky bottom bar
    const stickyObserver = new IntersectionObserver(([entry]) => {
      setShowStickyBar(entry.isIntersecting || entry.boundingClientRect.top < 0);
    }, {
      threshold: 0.1
    });
    const compraSection = document.getElementById('compra');
    if (compraSection) stickyObserver.observe(compraSection);
    return () => {
      revealObserver.disconnect();
      stickyObserver.disconnect();
    };
  }, []);
  // Header visibility on scroll
  useEffect(() => {
    let lastScrollY = window.scrollY;
    const handleScroll = () => {
      const currentScrollY = window.scrollY;
      if (currentScrollY > lastScrollY && currentScrollY > 100) {
        setScrollDirection('down');
        setIsManuallyExpanded(false);
      } else {
        setScrollDirection('up');
      }
      lastScrollY = currentScrollY;
      setHeaderVisible(currentScrollY > 100);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);
  useEffect(() => {
    const observer = new IntersectionObserver(entries => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          if (entry.target.id === 'fears-section') setFearsVisible(true);
          if (entry.target.id === 'problem-section') setProblemVisible(true);
          if (entry.target.id === 'problem-real-section') setProblemRealVisible(true);
          if (entry.target.id === 'decision-section') setDecisionVisible(true);
          if (entry.target.id === 'benefits-section') setBenefitsVisible(true);
          if (entry.target.id === 'post-hero-section') setPostHeroVisible(true);
        }
      });
    }, {
      threshold: 0.15
    });
    const sections = ['fears-section', 'problem-section', 'problem-real-section', 'decision-section', 'benefits-section', 'post-hero-section'];
    sections.forEach(id => {
      const el = document.getElementById(id);
      if (el) observer.observe(el);
    });
    return () => observer.disconnect();
  }, []);
  const scrollToPurchase = () => {
    document.getElementById('compra')?.scrollIntoView({
      behavior: 'smooth'
    });
  };
  const scrollToSolution = () => {
    document.getElementById('solution')?.scrollIntoView({
      behavior: 'smooth'
    });
  };
  const handlePurchaseClick = () => {
    trackEvent('InitiateCheckout');
    window.location.href = 'https://checkout.maestroelectrico.shop/cart/47885775995102:1';
  };
  const handleComboPurchaseClick = () => {
    trackEvent('InitiateCheckout');
    window.location.href = 'https://checkout.maestroelectrico.shop/cart/47989263106270:1';
  };
  useEffect(() => {
    const observer = new IntersectionObserver(entries => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          trackEvent('ViewContent', {
            content_name: 'Bonuses Section'
          });
          observer.disconnect();
        }
      });
    }, {
      threshold: 0.5
    });
    const bonusSection = document.getElementById('bonuses');
    if (bonusSection) observer.observe(bonusSection);
    return () => observer.disconnect();
  }, [trackEvent]);
  const contentImages = [{
    image: 'https://cdn.magicpatterns.com/uploads/6N9aigZzaNsoRe37pWdoFH/WhatsApp_Image_2026-01-17_at_13.42.51.jpg',
    caption: 'Diagramas explicados paso a paso'
  }, {
    image: 'https://cdn.magicpatterns.com/uploads/aRdF2bWiMHEmctuHXsJVR9/WhatsApp_Image_2026-01-17_at_13.42.50_(3).jpg',
    caption: 'Entendé la lógica del circuito, no solo la conexión'
  }, {
    image: 'https://cdn.magicpatterns.com/uploads/fuLknpKftbKZSC5DGAQ2tZ/WhatsApp_Image_2026-01-17_at_13.42.50_(2).jpg',
    caption: 'Casos prácticos aplicados al trabajo real'
  }, {
    image: 'https://cdn.magicpatterns.com/uploads/aGWc1fPM9DTj6kfR5oKjFr/WhatsApp_Image_2026-01-17_at_13.42.50_(1).jpg',
    caption: 'Material pensado para uso profesional'
  }, {
    image: 'https://cdn.magicpatterns.com/uploads/dZNF3VrgumsrSXiMRXLWTh/WhatsApp_Image_2026-01-17_at_13.42.49.jpg',
    caption: 'Explicaciones técnicas claras y directas'
  }];
  const showMoreTestimonials = () => {
    setVisibleTestimonials(prev => Math.min(prev + 5, allChatReviews.length));
  };
  const handleSubmitReview = (review: {
    name: string;
    text: string;
    stars: number;
  }) => {
    setLocalReview(review);
  };
  const fearItems = [{
    icon: Flame,
    title: 'Tenés miedo de quemar un motor',
    desc: 'Un error de conexión y el equipo queda inutilizado.'
  }, {
    icon: AlertCircle,
    title: 'Cuando no responde',
    desc: 'Dás tensión… y el circuito no hace lo que debería.'
  }, {
    icon: Brain,
    title: 'La inseguridad te hace quedar mal frente al cliente',
    desc: 'No por el trabajo, sino por cómo lo enfrentás.'
  }, {
    icon: DollarSign,
    title: 'Estás estancado ganando siempre lo mismo',
    desc: 'porque te falta confianza para aceptar trabajos más grandes.'
  }];
  const problemItems = [{
    icon: BookX,
    title: 'Libros teóricos',
    desc: 'Mucho concepto, poca aplicación real.'
  }, {
    icon: Video,
    title: 'Videos aislados',
    desc: 'Sirven para un caso puntual, no cuando algo cambia.'
  }, {
    icon: HardHat,
    title: 'Experiencia sola',
    desc: 'Funciona… hasta que aparece un caso distinto.'
  }];
  const decisionItems = [{
    icon: CircuitBoard,
    text: 'Criterio claro para interpretar circuitos de mando',
    keyword: 'Criterio'
  }, {
    icon: Cpu,
    text: 'Lógica aplicada a motores y tableros reales',
    keyword: 'Lógica'
  }, {
    icon: Target,
    text: 'Menos dudas al montar y diagnosticar',
    keyword: 'diagnóstico'
  }, {
    icon: Settings,
    text: 'Decisiones técnicas, no intuición',
    keyword: 'Decisiones'
  }, {
    icon: Award,
    text: 'Material pensado para trabajo en campo',
    keyword: 'campo'
  }];
  return <div className="min-h-screen bg-gray-50 font-sans text-gray-800" data-id="element-75">
      {notification && !headerVisible && <PurchaseNotification name={notification.name} city={notification.city} pack={notification.pack} isVisible={isVisible} data-id="element-76" />}

      {/* Header - Hidden initially, appears on scroll */}
      <motion.header animate={{
      paddingTop: isVisible && notification ? '20px' : '12px',
      paddingBottom: isVisible && notification ? '20px' : '12px',
      backgroundColor: isVisible && notification ? '#1e3a8a' : '#172554',
      boxShadow: isVisible && notification ? '0 10px 25px -5px rgba(0, 0, 0, 0.3), 0 8px 10px -6px rgba(0, 0, 0, 0.3)' : '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)'
    }} className={`text-white fixed top-0 left-0 right-0 z-40 transition-all duration-400 ${headerVisible ? 'animate-header-slide' : 'opacity-0 -translate-y-full pointer-events-none'}`} data-id="element-77">
        <div className="container mx-auto px-4 flex justify-between items-center" data-id="element-78">
          <div className="flex items-center gap-3 overflow-hidden" data-id="element-79">
            <div className="w-10 h-10 rounded-full overflow-hidden border-2 border-white/20 animate-logo-pulse flex-shrink-0" data-id="element-80">
              <img src="https://cdn.magicpatterns.com/uploads/tA6D5wwQNzXrTuuBrNPdzP/capture_260206_165744.png" alt="Maestro Eléctrico Logo" className="w-full h-full object-cover" loading="lazy" decoding="async" width={40} height={40} data-id="element-81" />
            </div>

            <div className="relative h-10 flex items-center min-w-[200px]" data-id="element-82">
              <AnimatePresence mode="wait" data-id="element-83">
                {isVisible && notification ? <motion.div key={`${notification.name}-${notification.city}`} initial={{
                y: 20,
                opacity: 0,
                scale: 0.9
              }} animate={{
                y: 0,
                opacity: 1,
                scale: 1
              }} exit={{
                y: -20,
                opacity: 0,
                scale: 0.9
              }} transition={{
                type: 'spring',
                stiffness: 400,
                damping: 25
              }} className="flex items-center gap-2" data-id="element-84">
                    <div className="bg-green-500 p-1.5 rounded-full flex-shrink-0 shadow-lg shadow-green-500/20" data-id="element-85">
                      {notification.pack === 'solo' ? <Zap className="w-4 h-4 text-white fill-white" data-id="element-86" /> : <Flame className="w-4 h-4 text-white fill-white" data-id="element-87" />}
                    </div>
                    <div className="flex flex-col justify-center" data-id="element-88">
                      <p className="text-[11px] md:text-[12px] font-black leading-tight tracking-tight" data-id="element-89">
                        <span className="text-green-400" data-id="element-90">
                          {notification.name}
                        </span>{' '}
                        de {notification.city}
                      </p>
                      <p className="text-[9px] md:text-[10px] text-blue-100 font-bold uppercase tracking-wider" data-id="element-91">
                        compró{' '}
                        <span className="text-white" data-id="element-92">
                          {notification.pack === 'solo' ? 'Maestro Eléctrico' : 'Combo Maestro + PLC'}
                        </span>
                      </p>
                    </div>
                  </motion.div> : <motion.span key="default-text" initial={{
                opacity: 0
              }} animate={{
                opacity: 1
              }} className="font-bold tracking-tight text-[14px] text-blue-100/50" data-id="element-93">
                    Inscripciones abiertas
                  </motion.span>}
              </AnimatePresence>
            </div>
          </div>
          <button onClick={scrollToPurchase} className="bg-yellow-500 hover:bg-yellow-400 text-blue-900 font-bold py-2 px-4 rounded transition-colors text-[14px] flex-shrink-0 pl-[5px] pr-[5px]" data-id="element-94">
            Ver Oferta
          </button>
        </div>
      </motion.header>

      {/* TOP HERO IMAGE BANNER */}
      <div className="w-full leading-none" data-id="element-95"></div>

      {/* HERO - Radial gradient with vibrant blue center */}
      <section className="relative min-h-[90vh] flex items-center justify-center overflow-hidden py-8" style={{
      background: 'radial-gradient(ellipse at 50% 50%, #1e40af 0%, #1e3a8a 25%, #172554 50%, #0f1729 80%, #0a1120 100%)'
    }} data-id="element-96">
        <div className="container mx-auto px-4 relative z-10" data-id="element-97">
          {/* Small text above title */}
          <p className="text-center text-[11px] md:text-xs font-medium text-blue-300/70 uppercase tracking-[0.25em] mb-6 animate-hero-text" style={{
          animationDelay: '0s'
        }} data-id="element-98">
            PARA ELECTRICISTAS, TÉCNICOS Y MANTENIMIENTO INDUSTRIAL
          </p>

          {/* Main Title - Staggered fade */}
          <h1 className="text-4xl md:text-6xl lg:text-7xl font-extrabold mb-6 leading-[1.1] text-center" data-id="element-99">
            <span className="block animate-hero-text text-white" style={{
            animationDelay: '0.1s'
          }} data-id="element-100">
              CONVERTITE
            </span>
            <span className="block animate-hero-text text-yellow-400" style={{
            animationDelay: '0.3s'
          }} data-id="element-101">
              EN UN MAESTRO ELÉCTRICO
            </span>
          </h1>

          {/* Subtitle */}
          <p className="text-lg md:text-xl text-center mb-12 font-medium animate-hero-text text-blue-100" style={{
          animationDelay: '0.5s'
        }} data-id="element-102">
            ENTENDÉ EL CONTROL ELÉCTRICO Y TOMÁ{' '}
            <span className="text-yellow-300" data-id="element-103">TRABAJOS MEJORES PAGOS</span>
          </p>

          {/* Mockup - Premium with glow */}
          <div className="mb-10 flex justify-center relative animate-hero-zoom" style={{
          animationDelay: '0.7s'
        }} data-id="element-104">
            {/* Glow behind mockup */}
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[350px] h-[350px] bg-blue-500/30 rounded-full blur-[80px]" data-id="element-105"></div>
            <img src="https://cdn.magicpatterns.com/uploads/fMZ5Fq5SNbxYwzfww2mdPS/Diseno_sin_titulo_(1).webp" alt="Control Eléctrico Libro Digital" className="w-full max-w-xs md:max-w-sm drop-shadow-2xl cursor-pointer relative z-10 hover:scale-[1.02] transition-transform duration-700" onClick={scrollToPurchase} decoding="async" fetchPriority="high" width={384} height={448} data-id="element-106" />
          </div>

          {/* Stars + Social proof - Smaller */}
          <div className="text-center mb-8 animate-hero-text" style={{
          animationDelay: '0.9s'
        }} data-id="element-107">
            <div className="flex items-center justify-center gap-2 mb-2" data-id="element-108">
              <div className="flex items-center gap-0.5" data-id="element-109">
                {[1, 2, 3, 4, 5].map(s => <Star key={s} className="w-4 h-4 fill-yellow-400 text-yellow-400" data-id="element-110" />)}
              </div>
              <span className="text-xs font-medium text-blue-300 uppercase tracking-wide" data-id="element-111">
                (4.9/5)
              </span>
            </div>
            <p className="text-xs font-medium text-blue-300 uppercase tracking-wide" data-id="element-112">
              Más de 6.250 profesionales ya lo usan
            </p>
          </div>

          {/* CTA Button */}
          <div className="text-center animate-hero-text" style={{
          animationDelay: '1.1s'
        }} data-id="element-113">
            <button onClick={scrollToPurchase} className="btn-shine bg-yellow-500 hover:bg-yellow-400 text-blue-900 text-xl md:text-2xl font-bold py-4 px-10 rounded-lg shadow-lg transition-all inline-flex items-center gap-2 animate-cta-pulse hover:scale-105" data-id="element-114">
              EMPEZAR HOY MISMO ↓
            </button>
            <p className="mt-4 md:text-xs text-blue-300/50 uppercase tracking-widest animate-hero-text text-[8px]" style={{
            animationDelay: '1.3s'
          }} data-id="element-115">
              (descarga inmediata ∙ acceso permanente ∙ sin mensualidad)
            </p>
          </div>
        </div>
      </section>

      {/* POST-HERO - Antes vs Después Comparison */}
      <section id="post-hero-section" className="py-6 px-4" style={{
      background: '#0a1929'
    }} data-id="element-116">
        <div className="container mx-auto max-w-3xl" data-id="element-117">
          <div data-reveal className={`relative bg-gradient-to-br from-gray-800/90 to-blue-900/60 border border-yellow-500/30 rounded-2xl p-5 md:p-8 shadow-2xl overflow-hidden ${postHeroVisible ? '' : ''}`} data-id="element-118">
            <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[80%] h-[2px] bg-gradient-to-r from-transparent via-yellow-500/40 to-transparent" data-id="element-119"></div>

            {/* Comparison Table */}
            <div className="grid grid-cols-2 gap-0 mb-6" data-id="element-120">
              {/* Header */}
              <div className="bg-red-900/40 border-b border-r border-gray-600/30 p-3 text-center rounded-tl-lg" data-id="element-121">
                <p className="text-sm font-bold text-red-300 uppercase tracking-wide" data-id="element-122">
                  Instalador Común
                </p>
              </div>
              <div className="bg-yellow-600/20 border-b border-gray-600/30 p-3 text-center rounded-tr-lg" data-id="element-123">
                <p className="text-sm font-bold text-yellow-400 uppercase tracking-wide" data-id="element-124">
                  Maestro Eléctrico
                </p>
              </div>

              {/* Rows */}
              {[{
              left: 'Conecta cables por memoria',
              right: 'Entiende la lógica del circuito'
            }, {
              left: 'Copia diagramas sin comprenderlos',
              right: 'Lee e interpreta cualquier diagrama'
            }, {
              left: 'Se traba cuando algo cambia',
              right: 'Resuelve variantes con criterio'
            }, {
              left: 'Cobra lo mismo hace años',
              right: 'Toma trabajos más grandes y mejor pagos'
            }, {
              left: 'Depende de otro para validar',
              right: 'Tiene autoridad técnica frente al cliente'
            }].map((row, i) => <Fragment key={i} data-id="element-125">
                  <div className={`border-r border-gray-600/30 p-3 ${i < 4 ? 'border-b border-gray-600/20' : ''}`} data-id="element-126">
                    <div className="flex items-start gap-2" data-id="element-127">
                      <X className="w-4 h-4 text-red-400 flex-shrink-0 mt-0.5" data-id="element-128" />
                      <p className="text-sm text-gray-400" data-id="element-129">{row.left}</p>
                    </div>
                  </div>
                  <div className={`p-3 ${i < 4 ? 'border-b border-gray-600/20' : ''}`} data-id="element-130">
                    <div className="flex items-start gap-2" data-id="element-131">
                      <Check className="w-4 h-4 text-yellow-400 flex-shrink-0 mt-0.5" data-id="element-132" />
                      <p className="text-sm text-white font-medium" data-id="element-133">
                        {row.right}
                      </p>
                    </div>
                  </div>
                </Fragment>)}
            </div>

            {/* Closing statement */}
            <div className="text-center pt-4 border-t border-gray-700/50 flex flex-col items-center gap-3" data-id="element-134">
              <Lightbulb className="w-8 h-8 text-yellow-400 animate-bulb-glow" strokeWidth={1.5} data-id="element-135" />
              <p className="text-xl md:text-2xl font-extrabold text-yellow-400 leading-tight uppercase tracking-wide drop-shadow-lg" data-id="element-136">
                UN MAESTRO ELÉCTRICO RESUELVE EL CIRCUITO ANTES DE TOCARLO.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* EL PROBLEMA REAL - With animated background */}
      <section id="problem-real-section" className="py-8 px-4 bg-gradient-to-br from-gray-900 via-blue-950 to-gray-900 animate-gradient-slow relative overflow-hidden" data-id="element-137">
        {/* Subtle pattern */}
        <div className="absolute inset-0 bg-[url('https://www.transparenttextures.com/patterns/diagmonds-light.png')] opacity-[0.02]" data-id="element-138"></div>

        <div className="container mx-auto max-w-3xl relative z-10" data-id="element-139">
          <h3 className={`text-3xl md:text-4xl font-extrabold text-white mb-10 text-center uppercase tracking-wide transition-all duration-700 ${problemRealVisible ? 'animate-stagger-in' : 'opacity-0'}`} data-id="element-140">
            EL <span className="text-yellow-400" data-id="element-141">PROBLEMA</span> REAL
          </h3>

          <div className={`problem-card bg-gray-800/80 backdrop-blur-sm border-l-4 border-red-500 rounded-r-xl p-6 md:p-10 shadow-lg transition-all duration-700 ${problemRealVisible ? 'animate-stagger-in' : 'opacity-0'}`} style={{
          animationDelay: '0.15s'
        }} data-id="element-142">
            <p className="text-lg md:text-xl text-gray-300 mb-8 font-light" data-id="element-143">
              Sabés conectar componentes individuales.
            </p>

            <div className="bg-gray-700/40 border border-gray-600/50 p-6 rounded-lg mb-10 relative overflow-hidden" data-id="element-144">
              <div className="absolute top-0 left-0 w-1 h-full bg-red-500/50" data-id="element-145"></div>
              <div className="flex gap-4" data-id="element-146">
                <AlertTriangle className="w-6 h-6 text-yellow-500 flex-shrink-0 mt-1" data-id="element-147" />
                <p className="text-xl md:text-2xl font-bold text-white leading-relaxed" data-id="element-148">
                  Pero el problema aparece cuando no terminás de entender la
                  lógica completa del circuito.
                </p>
              </div>
            </div>

            <div className="space-y-6 mb-10" data-id="element-149">
              {['Conocés contactores, térmicos y pulsadores por separado.', 'La dificultad real surge al integrarlos en un sistema.', 'Esa falta de visión global genera errores y retrabajo.'].map((text, i) => <div key={i} className={`flex items-start gap-4 transition-all duration-500 ${problemRealVisible ? 'animate-stagger-in' : 'opacity-0'}`} style={{
              animationDelay: `${0.3 + i * 0.1}s`
            }} data-id="element-150">
                  <div className="w-6 h-6 rounded-full border border-gray-600 flex items-center justify-center flex-shrink-0 mt-0.5" data-id="element-151">
                    <div className="w-2 h-2 bg-red-400 rounded-full" data-id="element-152"></div>
                  </div>
                  <p className="text-gray-300 text-lg leading-relaxed" data-id="element-153">
                    {text}
                  </p>
                </div>)}
            </div>

            <div className="pt-8 border-t border-gray-700/50" data-id="element-154">
              <p className="text-lg font-medium text-gray-400 text-center italic opacity-90" data-id="element-155">
                "El problema no es falta de capacidad. Es que casi nadie enseña
                a entender la lógica real del control eléctrico."
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ¿TE PASA ESTO? - With animated background */}
      <section id="fears-section" className="py-8 px-4 bg-gradient-to-br from-gray-950 via-blue-950 to-gray-950 animate-gradient-slow relative overflow-hidden" data-id="element-156">
        <div className="absolute inset-0 bg-[url('https://www.transparenttextures.com/patterns/diagmonds-light.png')] opacity-[0.02]" data-id="element-157"></div>

        <div className="container mx-auto max-w-3xl relative z-10" data-id="element-158">
          <div className={`text-center mb-12 transition-all duration-700 ${fearsVisible ? 'animate-stagger-in' : 'opacity-0'}`} data-id="element-159">
            <h3 className="text-4xl md:text-5xl font-extrabold text-white mb-4 uppercase" data-id="element-160">
              ¿TE <span className="text-yellow-400" data-id="element-161">PASA</span> ESTO?
            </h3>
            <p className="text-lg text-gray-400" data-id="element-162">
              Si trabajás con tableros, motores y circuitos, esto es para vos.
            </p>
          </div>

          <div className={`problem-card bg-gray-800/80 backdrop-blur-sm rounded-2xl border-l-4 border-red-500 p-6 md:p-8 shadow-lg transition-all duration-700 ${fearsVisible ? 'animate-stagger-in' : 'opacity-0'}`} style={{
          animationDelay: '0.15s'
        }} data-id="element-163">
            <div className="space-y-6" data-id="element-164">
              {fearItems.map((item, i) => <div key={i} className={`flex items-start gap-4 pb-6 ${i < fearItems.length - 1 ? 'border-b border-gray-700' : ''} transition-all duration-500 ${fearsVisible ? 'animate-stagger-in' : 'opacity-0'}`} style={{
              animationDelay: `${0.3 + i * 0.1}s`,
              animationFillMode: 'forwards'
            }} data-id="element-165">
                  <div className="w-9 h-9 bg-gray-700 rounded-full flex items-center justify-center flex-shrink-0" data-id="element-166">
                    <item.icon className="w-4 h-4 text-red-400" data-id="element-167" />
                  </div>
                  <div data-id="element-168">
                    <h4 className="font-bold text-white text-lg mb-1" data-id="element-169">
                      {item.title}
                    </h4>
                    <p className="text-gray-400" data-id="element-170">{item.desc}</p>
                  </div>
                </div>)}
            </div>

            <div className="mt-8 pt-6 border-t border-gray-700 text-center" data-id="element-171">
              <p className="text-xl font-medium text-gray-300 mb-2" data-id="element-172">
                Ahí aparece la inseguridad.
              </p>
              <p className="text-xl font-bold text-white" data-id="element-173">
                Y en control eléctrico, la inseguridad{' '}
                <span className="text-yellow-400" data-id="element-174">se paga caro</span>.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* EL PROBLEMA NO SOS VOS - With animated background */}
      <section id="problem-section" className="py-8 px-4 bg-gradient-to-br from-gray-900 via-blue-950 to-gray-900 animate-gradient-slow relative overflow-hidden" data-id="element-175">
        <div className="absolute inset-0 bg-[url('https://www.transparenttextures.com/patterns/diagmonds-light.png')] opacity-[0.02]" data-id="element-176"></div>

        <div className="container mx-auto max-w-3xl relative z-10" data-id="element-177">
          <div className={`text-center mb-10 transition-all duration-700 ${problemVisible ? 'animate-stagger-in' : 'opacity-0'}`} data-id="element-178">
            <h3 className="text-3xl md:text-4xl font-extrabold text-white mb-2 uppercase" data-id="element-179">
              EL PROBLEMA <span className="text-yellow-400" data-id="element-180">NO SOS VOS</span>
            </h3>
            <p className="text-lg text-gray-400" data-id="element-181">
              La mayoría aprende partes sueltas, pero no el sistema completo.
            </p>
          </div>

          <div className={`problem-card bg-gray-800/80 backdrop-blur-sm rounded-2xl border-l-4 border-red-500 p-6 md:p-8 shadow-lg transition-all duration-700 ${problemVisible ? 'animate-stagger-in' : 'opacity-0'}`} style={{
          animationDelay: '0.15s'
        }} data-id="element-182">
            <div className="space-y-5" data-id="element-183">
              {problemItems.map((item, i) => <div key={i} className={`flex items-start gap-4 ${i < problemItems.length - 1 ? 'pb-5 border-b border-gray-700' : ''} transition-all duration-500 ${problemVisible ? 'animate-stagger-in' : 'opacity-0'}`} style={{
              animationDelay: `${0.3 + i * 0.1}s`,
              animationFillMode: 'forwards'
            }} data-id="element-184">
                  <div className="w-9 h-9 bg-gray-700 rounded-full flex items-center justify-center flex-shrink-0" data-id="element-185">
                    <item.icon className="w-4 h-4 text-red-400" data-id="element-186" />
                  </div>
                  <div data-id="element-187">
                    <h4 className="font-bold text-white text-lg mb-1" data-id="element-188">
                      {item.title}
                    </h4>
                    <p className="text-gray-400" data-id="element-189">{item.desc}</p>
                  </div>
                </div>)}
            </div>

            <div className="mt-8 pt-6 border-t border-gray-700 text-center" data-id="element-190">
              <p className="text-lg font-bold text-white" data-id="element-191">
                En casi ningún lado te enseñan a pensar el control eléctrico
                como un{' '}
                <span className="text-yellow-400" data-id="element-192">sistema completo</span>.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* SOLUTION SECTION */}
      <section id="solution" className="py-8 px-4 bg-gradient-to-b from-blue-900 to-blue-800 text-white relative overflow-hidden" data-id="element-193">
        <div className="absolute inset-0 bg-[url('https://www.transparenttextures.com/patterns/circuit-board.png')] opacity-10" data-id="element-194"></div>
        <div className="container mx-auto max-w-4xl relative z-10" data-id="element-195">
          <div className="text-center mb-12" data-id="element-196">
            <span className="bg-yellow-500 text-blue-900 font-bold px-4 py-2 rounded-full text-sm uppercase tracking-wider mb-4 inline-block" data-id="element-197">
              La Solución Definitiva
            </span>
            <h2 className="text-4xl md:text-5xl font-extrabold mb-6 uppercase" data-id="element-198">
              AHÍ NACE{' '}
              <span className="text-yellow-400" data-id="element-199">MAESTRO ELÉCTRICO</span>
            </h2>
            <p className="text-xl text-blue-100 max-w-2xl mx-auto" data-id="element-200">
              Un manual práctico creado para ayudarte a:
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8 items-center mb-12" data-id="element-201">
            <div className="space-y-4" data-id="element-202">
              {['Entender la lógica de los circuitos de mando', 'Leer diagramas con seguridad', 'Aplicar lo aprendido en motores y tableros reales', 'Dejar de memorizar esquemas y empezar a comprenderlos'].map((item, index) => <div key={index} className="flex items-start gap-3 bg-blue-800/50 p-4 rounded-lg border border-blue-700" data-id="element-203">
                  <Check className="text-yellow-400 w-6 h-6 flex-shrink-0 mt-0.5" data-id="element-204" />
                  <span className="text-lg" data-id="element-205">{item}</span>
                </div>)}
            </div>
            <div className="flex justify-center" data-id="element-206">
              <video src="https://cdn.magicpatterns.com/uploads/rHCx25VPdb8sHH7a3Qn4xF/0312 (1)(1).mp4" className="w-full max-w-sm drop-shadow-2xl rounded-lg" autoPlay loop muted playsInline data-id="element-207" />
            </div>
          </div>

          <div className="bg-white/10 backdrop-blur-sm border border-white/20 rounded-xl p-8 text-center max-w-3xl mx-auto" data-id="element-208">
            <p className="text-xl italic leading-relaxed" data-id="element-209">
              "Este material funciona como un traductor entre el diagrama y el
              tablero real."
            </p>
          </div>
        </div>
      </section>

      {/* DECISION BLOCK */}
      <section id="decision-section" className="py-8 px-4 bg-gradient-to-br from-blue-900 via-blue-800 to-blue-900 text-white relative overflow-hidden" data-id="element-210">
        <div className="absolute inset-0 bg-[url('https://www.transparenttextures.com/patterns/carbon-fibre.png')] opacity-10" data-id="element-211"></div>
        <div className="container mx-auto max-w-4xl relative z-10" data-id="element-212">
          <div className="text-center mb-12" data-id="element-213">
            <h2 className="text-4xl md:text-5xl font-extrabold mb-4 leading-tight uppercase" data-id="element-214">
              <span className="text-yellow-400" data-id="element-215">CONTROL ELÉCTRICO</span>
            </h2>
            <p className="text-2xl md:text-3xl font-bold text-white" data-id="element-216">
              EL CRITERIO QUE TE FALTABA EN TABLEROS Y MOTORES
            </p>
          </div>

          <div className="max-w-3xl mx-auto mb-12" data-id="element-217">
            <div className="space-y-3" data-id="element-218">
              {decisionItems.map((item, index) => <div key={index} className={`flex items-center gap-4 p-4 rounded-lg bg-blue-800/40 border border-blue-600/40 transition-all duration-300 hover:bg-blue-700/50 hover:border-blue-500/50 hover:translate-y-[-2px] hover:shadow-lg ${decisionVisible ? 'animate-stagger-in' : 'opacity-0'}`} style={{
              animationDelay: `${index * 80}ms`,
              animationFillMode: 'forwards'
            }} data-id="element-219">
                  <div className="w-8 h-8 flex items-center justify-center flex-shrink-0" data-id="element-220">
                    <item.icon className="w-5 h-5 text-yellow-400" data-id="element-221" />
                  </div>
                  <span className="text-lg text-gray-100" data-id="element-222">
                    {item.text.split(item.keyword).map((part, i, arr) => <span key={i} data-id="element-223">
                        {part}
                        {i < arr.length - 1 && <span className="text-blue-300" data-id="element-224">{item.keyword}</span>}
                      </span>)}
                  </span>
                </div>)}
            </div>
          </div>

          <div className="text-center" data-id="element-225">
            <button onClick={scrollToPurchase} className="btn-shine bg-yellow-500 hover:bg-yellow-400 text-blue-900 text-2xl font-extrabold py-5 px-12 rounded-lg shadow-2xl hover:shadow-yellow-500/30 transition-all transform hover:scale-105" data-id="element-226">
              SÍ, QUIERO LA OFERTA AHORA
            </button>
          </div>
        </div>
      </section>

      {/* BONUSES - Carousel */}
      <section id="bonuses" className="py-8 px-4 bg-[#00005E]" data-id="element-227">
        <div className="container mx-auto max-w-6xl" data-id="element-228">
          <div className="text-center mb-12" data-reveal data-id="element-229">
            <h2 className="text-3xl md:text-5xl font-bold text-white mb-4 tracking-tight relative inline-block" data-id="element-230">
              SI ACCEDÉS HOY, TE LLEVÁS ESTOS 5 BONUS DE REGALO
              <span className="absolute -bottom-2 left-1/2 -translate-x-1/2 w-24 h-1 bg-gradient-to-r from-transparent via-yellow-500 to-transparent rounded-full" data-id="element-231"></span>
            </h2>
            <p className="text-lg text-gray-400 max-w-2xl mx-auto mb-6 mt-4" data-id="element-232">
              Complementos perfectos para tu crecimiento profesional
            </p>
            {/* Modified part begins */}
            {/* CSS Animation Auto-Scroll Carousel with drag support */}
            <div ref={marqueeContainerRef} className={`cursor-grab active:cursor-grabbing ${marqueePaused ? 'overflow-x-auto' : 'overflow-hidden'}`} style={{
            scrollbarWidth: 'none',
            msOverflowStyle: 'none',
            WebkitOverflowScrolling: 'touch'
          }} onMouseDown={e => handleDragStart(e.clientX)} onMouseMove={e => handleDragMove(e.clientX)} onMouseUp={handleDragEnd} onMouseLeave={handleDragEnd} onTouchStart={e => handleDragStart(e.touches[0].clientX)} onTouchMove={e => handleDragMove(e.touches[0].clientX)} onTouchEnd={handleDragEnd} data-id="element-233">
              <div className={`bonus-marquee-track flex gap-6 ${marqueePaused ? 'bonus-marquee-paused' : ''}`} data-id="element-234">
                {/* Render bonuses twice for seamless infinite loop */}
                {[...Array(2)].map((_, setIdx) => [{
                title: 'CURSO DE PANELES SOLARES',
                desc: 'Más de 50 clases grabadas para sumar una nueva especialidad altamente demandada en energía solar.',
                img: 'https://cdn.magicpatterns.com/uploads/w2rTPZ1b9h5D7JWz5dRRda/usa-la-imagen-adjunta-como-referencia-vi_LIZOyjwkQ0eu3ZbUzqbGFw_459-daMfTbW7XMmyjjHVYA_2k.png',
                value: '$32.500 ARS',
                exclusive: true,
                label: 'BONO EXCLUSIVO'
              }, {
                title: 'PLANTILLAS DE PRESUPUESTO EDITABLE',
                desc: 'Herramienta editable diseñada para trabajos eléctricos profesionales.',
                img: 'https://cdn.magicpatterns.com/uploads/52EEtPjWC34BYh7Nk8wekR/Prompt__A_high-end,_202604151035.webp',
                value: '$6.990 ARS',
                exclusive: false,
                label: 'HOY: GRATIS'
              }, {
                title: 'PROTOCOLO DE VERIFICACIÓN PREVIA A LA ENERGIZACIÓN',
                desc: 'Documento técnico estructurado que establece criterios de verificación.',
                img: 'https://cdn.magicpatterns.com/uploads/ecDoD4jMmyVG3yjoo5c8Tn/Prompt__A_detailed,_202604151040.webp',
                value: '$9.990 ARS',
                exclusive: false,
                label: 'HOY: GRATIS'
              }, {
                title: 'EFICIENCIA EN USO INDUSTRIAL DE MOTORES ELÉCTRICOS',
                desc: 'Material técnico orientado al uso correcto de motores eléctricos en entornos industriales.',
                img: 'https://cdn.magicpatterns.com/uploads/nH726tHAQweq2rPqiPh3zm/A_detailed,_premium_202604151030.webp',
                value: '$14.990 ARS',
                exclusive: false,
                label: 'HOY: GRATIS'
              }, {
                title: 'REGLAMENTACIONES AEA - ASOCIACIÓN ELECTROTÉCNICA ARGENTINA',
                desc: 'Manual esencial sobre las normativas vigentes de la AEA para instalaciones eléctricas seguras y profesionales.',
                img: 'https://cdn.magicpatterns.com/uploads/xz24GCFVSGY7FVuNDfknEw/necesito_hacer_el_202604151258.jpg',
                value: '$12.500 ARS',
                exclusive: false,
                label: 'HOY: GRATIS'
              }].map((bonus, i) => <div key={`${setIdx}-${i}`} onClick={scrollToPurchase} className="cursor-pointer flex-shrink-0 w-[280px] md:w-[300px]" data-id="element-235">
                      <div className={`bg-gray-950 rounded-xl overflow-hidden border-2 h-full flex flex-col ${bonus.exclusive ? 'border-yellow-400 shadow-xl shadow-yellow-500/10' : 'border-yellow-600/50 shadow-lg'}`} data-id="element-236">
                        <div className={`${bonus.exclusive ? 'bg-gradient-to-r from-yellow-600 to-yellow-500 text-black' : 'bg-gradient-to-r from-yellow-700 to-yellow-600 text-black'} px-4 py-2 text-center font-bold text-sm uppercase`} data-id="element-237">
                          {bonus.label}
                        </div>
                        <div className="p-5 flex flex-col flex-grow" data-id="element-238">
                          <div className="aspect-[3/4] mb-4 bg-black rounded-lg overflow-hidden relative" data-id="element-239">
                            {bonus.exclusive && <div className="absolute top-2 left-2 bg-yellow-500 text-black text-[10px] font-bold uppercase px-2 py-1 rounded z-10 tracking-wider" data-id="element-240">
                                BONO PREMIUM
                              </div>}
                            <img src={bonus.img} alt={bonus.title} className="w-full h-full object-cover" loading="lazy" decoding="async" width={300} height={400} data-id="element-241" />
                          </div>
                          <h3 className={`font-bold text-sm ${bonus.exclusive ? 'text-yellow-400' : 'text-white'} mb-3 leading-tight uppercase`} data-id="element-242">
                            {bonus.title}
                          </h3>
                          <p className="text-sm text-gray-400 mb-4 leading-relaxed flex-grow" data-id="element-243">
                            {bonus.desc}
                          </p>
                          <p className="text-sm text-gray-500 mb-2" data-id="element-244">
                            Valor:{' '}
                            <span className={`line-through ${bonus.exclusive ? 'text-red-400 font-medium' : 'text-gray-500'}`} data-id="element-245">
                              {bonus.value}
                            </span>
                          </p>
                          <div className={`${bonus.exclusive ? 'bg-yellow-500/10 border-yellow-500/30' : 'bg-green-500/10 border-green-500/30'} border rounded-lg px-3 py-2 mt-auto`} data-id="element-246">
                            <p className={`text-sm font-bold ${bonus.exclusive ? 'text-yellow-400' : 'text-green-400'}`} data-id="element-247">
                              ✓ Incluido sin costo adicional
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>))}
              </div>
            </div>
            <p className="text-center text-sm mt-3" data-id="element-248">
              <span className="text-yellow-400" data-id="element-249">←</span>
              <span className="text-gray-400 mx-2" data-id="element-250">
                DESLIZÁ PARA VER MÁS BONOS
              </span>
              <span className="text-yellow-400" data-id="element-251">→</span>
            </p>
          </div>
          {/* Modified part ends */}
        </div>
      </section>

      {/* EXTRA BONUS - CERTIFICATE */}
      <section className="py-8 px-4 bg-gradient-to-b from-[#0a1a3a] to-[#061229] relative overflow-hidden" data-id="element-252">
        <div className="absolute inset-0 bg-[url('https://www.transparenttextures.com/patterns/diagmonds-light.png')] opacity-[0.02]" data-id="element-253"></div>
        <div className="container mx-auto max-w-2xl relative z-10" data-id="element-254">
          <div className="flex flex-col items-center text-center" data-id="element-255">
            {/* Certificate Image at the top with frame */}
            <div className="relative group w-full mb-6" data-id="element-256">
              <div className="absolute -inset-1 bg-blue-500/10 rounded-xl blur-lg opacity-0 group-hover:opacity-100 transition-opacity duration-700" data-id="element-257"></div>
              <div className="relative bg-[#1a2b4a] border-[6px] border-[#2a3b5a] p-2 md:p-4 rounded-xl shadow-2xl overflow-hidden" data-id="element-258">
                <img src="https://cdn.magicpatterns.com/uploads/wgzBvYCj2MeubudywG2qNn/Diseno_sin_titulo_(2).png" alt="Certificado Maestro Eléctrico" className="w-full h-auto rounded-sm" loading="lazy" decoding="async" data-id="element-259" />
              </div>
            </div>

            {/* Badge */}
            <span className="bg-yellow-500 text-blue-900 font-black px-3 py-1 rounded text-[10px] uppercase tracking-widest mb-4 shadow-lg" data-id="element-260">
              BONO ESPECIAL
            </span>

            {/* Title */}
            <h2 className="text-xl md:text-3xl font-extrabold text-white mb-3 leading-tight uppercase tracking-tight" data-id="element-261">
              Certificado Profesional en <br className="hidden md:block" data-id="element-262" />
              Comandos Eléctricos
            </h2>

            {/* Description */}
            <p className="text-xs md:text-sm text-blue-100 max-w-xl mb-6 leading-relaxed opacity-80" data-id="element-263">
              Al completar la guía, recibís un certificado digital de alta
              calidad listo para compartir con tus clientes y en redes sociales.
              Incluye una guía de una página sobre cómo usarlo para generar
              confianza y conseguir más trabajos.
            </p>

            {/* Included Gratis Badge */}
            <div className="inline-flex items-center gap-2 bg-blue-900/40 border border-blue-400/30 px-4 py-1.5 rounded-full" data-id="element-264">
              <Check className="w-3.5 h-3.5 text-blue-400 stroke-[3px]" data-id="element-265" />
              <span className="text-blue-100 font-bold uppercase tracking-wider text-[10px]" data-id="element-266">
                Incluido Gratis con tu compra
              </span>
            </div>
          </div>
        </div>
      </section>

      {/* LO QUE TE LLEVÁS */}
      <section id="benefits-section" className="py-8 px-4 bg-gradient-to-b from-gray-50 to-white" data-id="element-267">
        <div className="container mx-auto max-w-5xl" data-id="element-268">
          <h2 className="md:text-4xl text-blue-900 mb-10 text-[28px] font-[700] font-[Poppins,_sans-serif]" data-id="element-269">
            LO QUE TE LLEVÁS CON MAESTRO ELÉCTRICO
          </h2>

          <div className="grid md:grid-cols-3 gap-6" data-id="element-270">
            {[{
            icon: Shield,
            title: 'CRITERIO Y SEGURIDAD',
            items: ['Más seguridad frente a los diagramas', 'Más criterio técnico'],
            tag: 'BASE TÉCNICA'
          }, {
            icon: Target,
            title: 'MENOS ERRORES',
            items: ['Menos errores', 'Menos dudas'],
            tag: 'MENOS RETRABAJO'
          }, {
            icon: TrendingUp,
            title: 'PERFIL PROFESIONAL',
            items: ['Más confianza profesional'],
            tag: 'MEJOR IMAGEN ANTE EL CLIENTE'
          }].map((card, i) => <div key={i} className={`bg-white border border-blue-200 rounded-xl p-6 shadow-md transition-all duration-300 hover:shadow-lg hover:-translate-y-1 ${benefitsVisible ? 'animate-stagger-in' : 'opacity-0'}`} style={{
            animationDelay: `${i * 100}ms`,
            animationFillMode: 'forwards'
          }} data-id="element-271">
                <div className="flex items-center gap-2 mb-4" data-id="element-272">
                  <card.icon className="w-5 h-5 text-blue-600" data-id="element-273" />
                  <h3 className="font-bold text-gray-900" data-id="element-274">{card.title}</h3>
                </div>
                <div className="space-y-3 mb-4" data-id="element-275">
                  {card.items.map((item, j) => <div key={j} className="flex items-start gap-2" data-id="element-276">
                      <Check className="w-4 h-4 text-blue-500 mt-0.5 flex-shrink-0" data-id="element-277" />
                      <span className="text-gray-700 text-sm" data-id="element-278">{item}</span>
                    </div>)}
                </div>
                <span className="inline-block bg-blue-100 text-blue-700 text-xs font-medium px-3 py-1 rounded-full" data-id="element-279">
                  {card.tag}
                </span>
              </div>)}
          </div>

          <div className="mt-12 bg-gradient-to-r from-blue-900 to-blue-800 text-white p-8 rounded-xl text-center shadow-lg" data-id="element-280">
            <p className="text-xl font-semibold leading-relaxed font-[Poppins,_sans-serif]" data-id="element-281">
              Invertís una vez en criterio. Eso se traduce en más trabajos,
              mejores decisiones y clientes que confían en vos.
            </p>
          </div>
        </div>
      </section>

      {/* SOCIAL PROOF CAROUSEL */}
      <section className="py-12 px-4 bg-gray-100 overflow-hidden" data-id="element-282">
        <div className="container mx-auto max-w-6xl" data-id="element-283">
          <div className="text-center mb-10" data-id="element-284">
            <h2 className="text-3xl md:text-4xl font-bold text-blue-900 mb-4 font-[Poppins,_sans-serif]" data-id="element-285">
              LO QUE DICEN OTROS PROFESIONALES
            </h2>
            <p className="text-gray-600" data-id="element-286">
              Más de 6.250 electricistas ya están aplicando este criterio
              técnico
            </p>
          </div>

          <div className="relative" data-id="element-287">
            <div className="flex gap-6 overflow-x-auto pb-8 snap-x snap-mandatory hide-scrollbar" data-id="element-288">
              {testimonialData.map((t, i) => <div key={i} className="flex-shrink-0 w-[300px] md:w-[400px] bg-white rounded-2xl shadow-xl border border-gray-100 overflow-hidden snap-center flex flex-col" data-id="element-289">
                  <div className="aspect-square overflow-hidden relative" data-id="element-290">
                    <img src={t.image} alt={t.name} className="w-full h-full object-cover" loading="lazy" decoding="async" width={1080} height={1080} data-id="element-291" />
                    <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent p-4" data-id="element-292">
                      <p className="text-white font-bold text-lg" data-id="element-293">{t.name}</p>
                      <p className="text-blue-300 text-sm font-medium" data-id="element-294">
                        {t.study}
                      </p>
                    </div>
                  </div>
                  <div className="p-6 flex-grow flex flex-col" data-id="element-295">
                    <div className="flex items-center gap-0.5 mb-4" data-id="element-296">
                      {[1, 2, 3, 4, 5].map(s => <Star key={s} className="w-4 h-4 fill-yellow-400 text-yellow-400" data-id="element-297" />)}
                    </div>
                    <p className="text-gray-700 italic mb-6 flex-grow" data-id="element-298">
                      "{t.comment}"
                    </p>
                    <div className="pt-4 border-t border-gray-100" data-id="element-299">
                      <p className="text-xs text-gray-400 uppercase tracking-wider font-bold mb-1" data-id="element-300">
                        Estudió en:
                      </p>
                      <p className="text-sm text-gray-600 font-medium" data-id="element-301">
                        {t.study}
                      </p>
                    </div>
                  </div>
                </div>)}
            </div>

            {/* Visual indicators for mobile */}
            <div className="flex justify-center gap-2 mt-2 md:hidden" data-id="element-302">
              {testimonialData.map((_, i) => <div key={i} className="w-1.5 h-1.5 rounded-full bg-gray-300" data-id="element-303"></div>)}
            </div>

            <p className="text-center text-sm text-gray-400 mt-4 flex items-center justify-center gap-2" data-id="element-304">
              <ChevronLeft className="w-4 h-4" data-id="element-305" /> Deslizá para ver más{' '}
              <ChevronRight className="w-4 h-4" data-id="element-306" />
            </p>
          </div>
        </div>
      </section>

      {/* PURCHASE SECTION - TWO CARDS */}
      <section id="compra" className="py-10 bg-white relative overflow-hidden" data-id="element-307">
        <div className="container mx-auto px-4 relative z-10" data-id="element-308">
          <div className="max-w-4xl mx-auto" data-id="element-309">
            <div className="text-center mb-8" data-id="element-310">
              <h2 className="text-3xl md:text-5xl font-black text-gray-900 mb-2 uppercase tracking-tight" data-id="element-311">
                ELEGÍ TU PACK Y EMPEZÁ HOY
              </h2>
              <p className="text-lg text-gray-600 font-medium" data-id="element-312">
                Acceso inmediato y de por vida a todo el material
              </p>
            </div>

            {/* New Reference-style Offer Container */}
            <div className={`max-w-md mx-auto rounded-3xl shadow-[0_20px_50px_rgba(0,0,0,0.1)] border overflow-hidden mb-8 transition-colors duration-500 ${selectedOffer === 'solo' ? 'bg-[#EFF6FF] border-blue-200' : 'bg-white border-gray-100'}`} data-id="element-313">
              {/* Orange Header */}
              <div className="py-3 text-center bg-[#172554]" data-id="element-314">
                <span className="text-white font-black text-lg uppercase tracking-wider" data-id="element-315">
                  DESCUENTO ESPECIAL
                </span>
              </div>

              <div className="p-4 md:p-6" data-id="element-316">
                {/* Offer Cards */}
                <div className="space-y-4 mb-8" data-id="element-317">
                  {/* Combo Card */}
                  <button onClick={() => setSelectedOffer('combo')} className={`w-full relative flex items-center gap-4 p-4 rounded-2xl border-2 transition-all duration-300 ${selectedOffer === 'combo' ? 'border-[#FFD700] bg-[#FFFDE7]' : 'border-gray-100 bg-white hover:border-gray-200'}`} data-id="element-318">
                    {/* Badge */}
                    <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-[#FFD700] text-black text-[10px] font-black px-3 py-1 rounded-full flex items-center gap-1 shadow-sm" data-id="element-319">
                      <Star className="w-3 h-3 fill-black" data-id="element-320" />
                      MÁS ELEGIDA
                    </div>

                    <div className="w-20 h-20 flex-shrink-0 bg-gray-50 rounded-lg overflow-hidden border border-gray-100" data-id="element-321">
                      <img src="https://cdn.magicpatterns.com/uploads/dmKXLs5VqNqowzaUTF8g14/aasdadasdasd.webp" alt="Combo Pack" className="w-full h-full object-contain" data-id="element-322" />
                    </div>

                    <div className="flex-1 text-left" data-id="element-323">
                      <h4 className="text-sm font-black text-gray-900 leading-tight uppercase" data-id="element-324">
                        COMBO PLC MASTER + MAESTRO ELÉCTRICO
                      </h4>
                      <p className="text-[10px] text-gray-500 font-bold uppercase mt-1" data-id="element-325">
                        Programa Completo + 10 Bonos
                      </p>
                    </div>

                    <div className="text-right flex flex-col items-end gap-2" data-id="element-326">
                      <span className="text-[#D4AF37] font-black text-lg" data-id="element-327">
                        $29.990
                      </span>
                      <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${selectedOffer === 'combo' ? 'border-[#FFD700]' : 'border-gray-300'}`} data-id="element-328">
                        {selectedOffer === 'combo' && <div className="w-2.5 h-2.5 rounded-full bg-[#FFD700]" data-id="element-329"></div>}
                      </div>
                    </div>
                  </button>

                  {/* Solo Card */}
                  <button onClick={() => setSelectedOffer('solo')} className={`w-full relative flex items-center gap-4 p-4 rounded-2xl border-2 transition-all duration-300 ${selectedOffer === 'solo' ? 'border-blue-500 bg-blue-50/30' : 'border-gray-100 bg-white hover:border-gray-200'}`} data-id="element-330">
                    <div className="w-20 h-20 flex-shrink-0 bg-gray-50 rounded-lg overflow-hidden border border-gray-100" data-id="element-331">
                      <img src="https://cdn.magicpatterns.com/uploads/jCvJXVKKtEEbjo3GpY2vii/ChatGPT_Image_6_feb_2026,_05_30_12_p.m..png" alt="Solo Maestro" className="w-full h-full object-contain" data-id="element-332" />
                    </div>

                    <div className="flex-1 text-left" data-id="element-333">
                      <h4 className="text-sm font-black text-gray-900 leading-tight uppercase" data-id="element-334">
                        SOLO MAESTRO ELÉCTRICO
                      </h4>
                      <p className="text-[10px] text-gray-500 font-bold uppercase mt-1" data-id="element-335">
                        Sistema Avanzado + 6 Bonos
                      </p>
                    </div>

                    <div className="text-right flex flex-col items-end gap-2" data-id="element-336">
                      <span className="text-gray-900 font-black text-lg" data-id="element-337">
                        $19.990
                      </span>
                      <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${selectedOffer === 'solo' ? 'border-blue-500' : 'border-gray-300'}`} data-id="element-338">
                        {selectedOffer === 'solo' && <div className="w-2.5 h-2.5 rounded-full bg-blue-500" data-id="element-339"></div>}
                      </div>
                    </div>
                  </button>
                </div>

                {/* Price Summary */}
                <div className="text-center mb-6" data-id="element-340">
                  <p className="text-sm text-[#FF5C5C] font-bold mb-1" data-id="element-341">
                    Antes{' '}
                    <span className="line-through" data-id="element-342">
                      {selectedOffer === 'solo' ? '$49.990' : '$99.980'} ARS
                    </span>
                  </p>
                  <div className="flex items-center justify-center gap-2" data-id="element-343">
                    <span className="text-5xl md:text-6xl font-black text-[#00A859]" data-id="element-344">
                      {selectedOffer === 'solo' ? '$19.990' : '$29.990'}
                    </span>
                    <span className={`font-bold text-sm transition-colors duration-500 ${selectedOffer === 'solo' ? 'text-blue-400' : 'text-gray-400'}`} data-id="element-345">
                      ARS
                    </span>
                  </div>
                </div>

                {/* Installments Badge */}
                <div className="flex justify-center mb-8" data-id="element-346">
                  <div className="bg-[#E8F5E9] text-[#2E7D32] border border-[#A5D6A7] px-4 py-2 rounded-xl flex items-center gap-2 text-sm font-bold animate-badge-reflection" data-id="element-347">
                    <CreditCard className="w-4 h-4" data-id="element-348" />
                    Hasta 3 cuotas sin interés
                  </div>
                </div>

                {/* Checklist Section */}
                <div className={`rounded-3xl p-6 transition-colors duration-500 ${selectedOffer === 'solo' ? 'bg-blue-100/50' : 'bg-[#F8F9FA]'}`} data-id="element-349">
                  <h5 className={`font-black text-xs uppercase tracking-widest mb-4 text-center transition-colors duration-500 ${selectedOffer === 'solo' ? 'text-blue-900/40' : 'text-[#9E9E9E]'}`} data-id="element-350">
                    ¿QUÉ ESTÁS LLEVANDO?
                  </h5>
                  <div className="space-y-3" data-id="element-351">
                    {(selectedOffer === 'combo' ? ['Manual Maestro Eléctrico', 'Manual PLC Master (Industrial)', 'Bono 1: Eficiencia en Motores', 'Bono 2: Plantillas Presupuesto', 'Bono 3: Protocolo de Energia', 'Bono 4: Curso Paneles Solares', 'Bono 5: Reglamentaciones AEA', 'EXTRA: Certificado Profesional', 'Bono 7: Curso completo de SCADA', 'Bono 8: Módulo extra de PLC', 'Bono 9: Control de procesos', 'Bono 10: Variadores y Sensores'] : ['Manual Maestro Eléctrico', 'Bono 1: Eficiencia en Motores', 'Bono 2: Plantillas Presupuesto', 'Bono 3: Protocolo de Energia', 'Bono 4: Curso Paneles Solares', 'Bono 5: Reglamentaciones AEA', 'EXTRA: Certificado Profesional']).map((item, i) => <div key={i} className="flex items-center gap-3" data-id="element-352">
                        <div className="w-5 h-5 rounded-full bg-[#00A859] flex items-center justify-center flex-shrink-0" data-id="element-353">
                          <Check className="w-3 h-3 text-white stroke-[4px]" data-id="element-354" />
                        </div>
                        <span className={`font-bold text-sm transition-colors duration-500 ${selectedOffer === 'solo' ? 'text-blue-900' : 'text-gray-700'}`} data-id="element-355">
                          {item}
                        </span>
                      </div>)}
                  </div>
                </div>

                {/* CTA Button */}
                <button onClick={selectedOffer === 'combo' ? handleComboPurchaseClick : handlePurchaseClick} className="w-full hover:bg-[#008A20] text-white text-lg font-black py-5 rounded-2xl shadow-xl transition-all mt-8 flex items-center justify-center gap-2 uppercase tracking-tight bg-[#00B62B]" data-id="element-356">
                  SÍ, QUIERO ACCEDER AHORA →
                </button>

                {/* Social Proof Widget */}
                <div className="mt-6 rounded-2xl overflow-hidden border border-gray-700 shadow-lg" data-id="element-357">
                  {/* Date Banner */}
                  <div className="bg-[#0A1628] px-4 py-3 text-center border-b border-yellow-500/30" data-id="element-358">
                    <p className="text-white text-xs md:text-sm font-black tracking-wide uppercase" data-id="element-359">
                      OFERTA VÁLIDA SOLO POR HOY:{' '}
                      <span className="text-yellow-400" data-id="element-360">
                        {weekdayDate.toUpperCase()}
                      </span>
                    </p>
                  </div>

                  {/* Viewers & Cupos Bar */}
                  <div className="bg-[#0D1B2A] grid grid-cols-2 divide-x divide-gray-700" data-id="element-361">
                    {/* Viewers */}
                    <div className="flex items-center gap-3 px-4 py-4 justify-center" data-id="element-362">
                      <div className="flex items-center gap-1.5" data-id="element-363">
                        <span className="relative flex h-3 w-3" data-id="element-364">
                          <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75" data-id="element-365"></span>
                          <span className="relative inline-flex rounded-full h-3 w-3 bg-green-500" data-id="element-366"></span>
                        </span>
                        <span className="text-green-400 text-2xl md:text-3xl font-black" data-id="element-367">
                          {viewers}
                        </span>
                      </div>
                      <div className="text-left" data-id="element-368">
                        <p className="text-gray-300 text-[10px] md:text-xs font-bold leading-tight uppercase" data-id="element-369">
                          PERSONAS
                          <br data-id="element-370" />
                          MIRANDO
                          <br data-id="element-371" />
                          EN ESTE MOMENTO
                        </p>
                      </div>
                    </div>

                    {/* Cupos */}
                    <div className="flex items-center gap-3 px-4 py-4 justify-center" data-id="element-372">
                      <div className="flex items-center gap-1.5" data-id="element-373">
                        <span className="text-red-400 text-2xl md:text-3xl font-black" data-id="element-374">
                          {selectedOffer === 'combo' ? comboSpots : urgencySpots}
                        </span>
                      </div>
                      <div className="text-left" data-id="element-375">
                        <p className="text-gray-300 text-[10px] md:text-xs font-bold leading-tight uppercase" data-id="element-376">
                          CUPOS
                          <br data-id="element-377" />
                          PROMOCIONALES
                          <br data-id="element-378" />
                          DISPONIBLES
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Buyer Counter */}
                  <div className="bg-[#0A1628] px-4 py-4 text-center border-t border-gray-700" data-id="element-379">
                    <motion.p key={selectedOffer === 'combo' ? comboBuyers : mainBuyers} initial={{
                    scale: 1.1,
                    opacity: 0.7
                  }} animate={{
                    scale: 1,
                    opacity: 1
                  }} transition={{
                    duration: 0.3
                  }} className="text-[#00FF00] text-4xl md:text-5xl font-black tracking-tight" data-id="element-380">
                      {(selectedOffer === 'combo' ? comboBuyers : mainBuyers).toLocaleString('es-AR')}
                    </motion.p>
                    <p className="text-gray-300 text-xs md:text-sm font-bold uppercase tracking-wider mt-1" data-id="element-381">
                      PROFESIONALES YA ACCEDIERON
                    </p>
                  </div>
                </div>

                {/* Trust Badges Section */}
                <div className="mt-8 flex flex-col items-center gap-6 animate-fade-in-up-soft" data-id="element-382">
                  {/* Modified part begins */}
                  <div className="w-full flex justify-center" data-id="element-383">
                    {/* Placeholder for the new images with adjusted heights */}
                    <img src="https://cdn.magicpatterns.com/uploads/45AKbXKjJnZm5Z1fHy751C/capture_260410_163646.png" alt="Pago Seguro Garantizado" className="w-full max-w-[320px] h-auto object-contain brightness-105" loading="lazy" data-id="element-384" />
                  </div>
                  <div className="flex items-center justify-center md:gap-10 gap-[10px]" data-id="element-385">
                    <img src="https://cdn.magicpatterns.com/uploads/6sMJ49nAT1B4ubV5kxhyZE/png-clipart-safe-purchase-payment-security-shop-shopping-online-store-e-commerce-sign-green-texta.png" alt="Compra Segura" className="h-20 md:h-24 w-auto object-contain" loading="lazy" data-id="element-386" />
                    <img src="https://cdn.magicpatterns.com/uploads/xuS8xz2qt356pzvFYDWisp/Selo_de_Garantia_de_7_Dias_PNG_Transparente_Sem_Fundo.png" alt="Garantía de 7 Días" className="h-32 md:h-40 w-auto object-contain" loading="lazy" data-id="element-387" />
                  </div>
                  {/* Modified part ends */}
                </div>
              </div>
            </div>

            {/* MercadoLíder Platinum Trust Seal */}
            <div className="flex justify-center" data-id="element-388">
              <img src="https://cdn.magicpatterns.com/uploads/wihdMwuqBpQKvri8XHgUZZ/Gemini_Generated_Image_jbp7w2jbp7w2jbp7.png" alt="MercadoLíder Platinum - 8413 ventas" className="w-full max-w-2xl object-contain" loading="lazy" decoding="async" width={672} height={100} data-id="element-389" />
            </div>

            {/* Lifetime Access Bonus Section */}
            <div className="mt-12 mb-8 flex justify-center px-4" data-id="element-390">
              <div className="w-full max-w-2xl bg-[#000D1A] rounded-[12px] border border-gray-800 shadow-2xl overflow-hidden relative group" data-id="element-391">
                {/* Subtle Glow Effect */}
                <div className="absolute inset-0 bg-blue-500/5 opacity-0 group-hover:opacity-100 transition-opacity duration-700" data-id="element-392"></div>

                <div className="p-8 md:p-12 flex flex-col items-center text-center relative z-10 pl-[15px] pr-[15px]" data-id="element-393">
                  {/* Authority Seal */}
                  <div className="relative mb-8" data-id="element-394">
                    <div className="absolute inset-0 bg-yellow-500/20 rounded-full blur-3xl animate-pulse" data-id="element-395"></div>
                    <div className="relative w-40 h-40 md:w-48 md:h-48 rounded-full shadow-[0_0_50px_rgba(234,179,8,0.3)] border-4 border-yellow-500/20 overflow-hidden" data-id="element-396">
                      <img src="https://cdn.magicpatterns.com/uploads/oLERSYzpMkuFusC7XS9pGH/Gemini_Generated_Image_ajg2w8ajg2w8ajg2.png" alt="Sello de Acceso Vitalicio" className="w-full h-full object-cover" data-id="element-397" />
                    </div>
                  </div>

                  {/* Text Hierarchy */}
                  <span className="text-[#00FF00] text-[10px] md:text-xs font-black tracking-[0.2em] uppercase mb-3" data-id="element-398">
                    BONO EXTRA EXCLUSIVO
                  </span>

                  <h3 className="text-white text-3xl md:text-4xl font-bold mb-4 tracking-tight" data-id="element-399">
                    Acceso Vitalicio
                  </h3>

                  <p className="text-gray-300 text-sm md:text-base leading-relaxed max-w-lg mb-8" data-id="element-400">
                    Olvidate de las mensualidades. Con un solo pago tenés acceso
                    de por vida a todo el material y a las futuras
                    actualizaciones sin pagar un centavo más.
                  </p>

                  {/* Status Component */}
                  <div className="inline-flex items-center gap-2 px-6 py-2.5 rounded-full bg-[#062010] border border-[#00FF00]/40 shadow-[0_0_15px_rgba(0,255,0,0.1)]" data-id="element-401">
                    <Check className="w-4 h-4 text-[#00FF00] stroke-[3px]" data-id="element-402" />
                    <span className="text-[#00FF00] text-xs md:text-sm font-black uppercase tracking-widest" data-id="element-403">
                      INCLUIDO GRATIS
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CONTENT PREVIEW */}
      <section className="py-8 px-4 bg-white" data-id="element-404">
        <div className="container mx-auto max-w-6xl" data-id="element-405">
          <div className="text-center mb-12" data-id="element-406">
            <h2 className="text-3xl md:text-4xl font-bold text-blue-900 mb-4" data-id="element-407">
              Así es el material por dentro
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto" data-id="element-408">
              Ejemplos reales de las páginas que vas a usar en el trabajo diario
            </p>
          </div>

          <div className="space-y-16" data-id="element-409">
            <div className="flex justify-center" data-id="element-410">
              <div className="w-full max-w-2xl bg-gray-900 rounded-xl overflow-hidden shadow-2xl" data-id="element-411">
                <video src="https://cdn.magicpatterns.com/uploads/2sNKU8uzEEtWZTuVPY2CeA/0126.undefined" className="w-full" autoPlay loop muted playsInline data-id="element-412" />
              </div>
            </div>
            <ImageCarousel images={contentImages} data-id="element-413" />
          </div>
        </div>
      </section>

      {/* TESTIMONIALS - WhatsApp style, right before FAQ */}
      <section className="py-8 px-4 bg-gradient-to-b from-gray-100 to-gray-50" data-id="element-414">
        <div className="container mx-auto max-w-2xl" data-reveal data-id="element-415">
          <div className="text-center mb-8" data-id="element-416">
            <div className="flex items-center justify-center gap-1 mb-2" data-id="element-417">
              {[1, 2, 3, 4, 5].map(s => <Star key={s} className="w-4 h-4 fill-yellow-400 text-yellow-400" data-id="element-418" />)}
            </div>
            <h2 className="text-lg font-bold text-gray-800 uppercase" data-id="element-419">
              MÁS DE 6.250 ELECTRICISTAS SATISFECHOS
            </h2>
            <p className="text-xs text-gray-500 mt-1" data-id="element-420">
              4,9/5 — 487 reseñas verificadas
            </p>
          </div>

          {/* WhatsApp-style chat bubbles */}
          <div className="space-y-4 mb-8" data-id="element-421">
            {allChatReviews.slice(0, visibleTestimonials).map((t, i) => <div key={i} className="flex items-start gap-3" data-id="element-422">
                <div className="w-9 h-9 bg-green-600 rounded-full flex items-center justify-center text-white font-bold text-sm flex-shrink-0" data-id="element-423">
                  {t.name.charAt(0)}
                </div>
                <div className="flex-grow" data-id="element-424">
                  <div className="flex items-center gap-2 mb-1" data-id="element-425">
                    <span className="font-bold text-sm text-gray-800" data-id="element-426">
                      {t.name}
                    </span>
                    {t.verified && <span className="text-[9px] bg-green-100 text-green-700 px-1.5 py-0.5 rounded-full font-medium" data-id="element-427">
                        ✓ Verificado
                      </span>}
                  </div>
                  <div className="chat-bubble" data-id="element-428">
                    <p className="text-sm text-gray-800 leading-relaxed" data-id="element-429">
                      {t.msg}
                    </p>
                    <p className="text-[10px] text-gray-400 text-right mt-1" data-id="element-430">
                      {t.time}
                    </p>
                  </div>
                </div>
              </div>)}
          </div>

          {visibleTestimonials < allChatReviews.length && <div className="text-center" data-id="element-431">
              <button onClick={showMoreTestimonials} className="btn-shine bg-gray-100 border border-gray-200 text-gray-600 hover:bg-gray-200 font-medium py-2.5 px-6 rounded-lg transition-colors text-sm" data-id="element-432">
                Cargar 5 comentarios más
              </button>
            </div>}

          <ReviewForm onSubmitReview={handleSubmitReview} data-id="element-433" />
        </div>
      </section>

      {/* FAQ */}
      <section className="py-8 px-4 bg-white" data-id="element-434">
        <div className="container mx-auto max-w-3xl" data-id="element-435">
          <div className="text-center mb-12" data-id="element-436">
            <h2 className="text-3xl md:text-4xl font-bold text-blue-900 mb-4" data-id="element-437">
              Resolvé acá tus dudas
            </h2>
            <p className="text-lg text-gray-600" data-id="element-438">
              Hacé clic para ver la respuesta que necesitás
            </p>
          </div>

          <div className="space-y-3" data-id="element-439">
            <FAQItem question="¿Qué tipo de contenido incluye CONTROL ELÉCTRICO?" answer="Incluye un manual completo de control eléctrico, con explicación clara de la lógica de los circuitos, diagramas reales y ejemplos aplicados al trabajo profesional." data-id="element-440" />
            <FAQItem question="¿Este material es solo para electricistas con experiencia?" answer="Está pensado tanto para electricistas con experiencia como para técnicos que quieren entender de verdad el control eléctrico y dejar de trabajar por repetición o memoria." data-id="element-441" />
            <FAQItem question="¿Voy a aprender a interpretar diagramas eléctricos?" answer="Sí. El foco principal es ayudarte a interpretar y entender diagramas de mando y control eléctrico, no solo copiarlos." data-id="element-442" />
            <FAQItem question="¿Incluye ejemplos prácticos reales?" answer="Sí. El contenido está orientado a situaciones reales de tableros, motores y circuitos usados en el trabajo diario." data-id="element-443" />
            <FAQItem question="¿En qué formato recibo el material?" answer="Todo el contenido se entrega en formato digital (PDF) con acceso inmediato." data-id="element-444" />
            <FAQItem question="¿Puedo imprimir el material?" answer="Sí. Podés imprimirlo para uso personal." data-id="element-445" />
            <FAQItem question="¿Cuánto tarda en llegar el material después de la compra?" answer="El acceso es inmediato una vez acreditado el pago." data-id="element-446" />
            <FAQItem question="¿Necesito conocimientos avanzados de electrónica o automatización?" answer="No. El material explica los conceptos de forma clara y progresiva, enfocándose en criterio y lógica." data-id="element-447" />
            <FAQItem question="¿El contenido me sirve para trabajar con más seguridad?" answer="Sí. El objetivo es que entiendas qué está pasando en el circuito para trabajar con más seguridad y confianza." data-id="element-448" />
            <FAQItem question="¿Tengo acceso para siempre al material?" answer="Sí. El acceso es permanente." data-id="element-449" />
            <FAQItem question="¿Es un pago único?" answer="Sí. Pagás una sola vez y accedés a todo el contenido y bonos incluidos." data-id="element-450" />
          </div>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="bg-gray-900 text-white py-12 px-4 mb-24 md:mb-32" data-id="element-451">
        <div className="container mx-auto max-w-6xl" data-id="element-452">
          <p className="text-center text-sm" data-id="element-453">
            © 2025 Maestro Eléctrico. Todos los derechos reservados.
          </p>
          <p className="text-center text-sm mt-2" data-id="element-454">
            Diagramas y circuitos de mando para profesionales.
          </p>
        </div>
      </footer>

      {/* STICKY BOTTOM BAR */}
      <AnimatePresence data-id="element-455">
        {showStickyBar && <motion.div initial={{
        y: 100,
        opacity: 0
      }} animate={{
        y: scrollDirection === 'down' && !isManuallyExpanded ? 85 : 0,
        opacity: 1,
        scale: scrollDirection === 'down' && !isManuallyExpanded ? 0.95 : 1
      }} exit={{
        y: 100,
        opacity: 0
      }} transition={{
        type: 'spring',
        stiffness: 300,
        damping: 30
      }} className="fixed bottom-0 left-0 right-0 z-50 p-4 md:p-6" data-id="element-456">
            <div className="container mx-auto max-w-2xl relative" data-id="element-457">
              {/* Manual Expand Button */}
              <AnimatePresence data-id="element-458">
                {scrollDirection === 'down' && !isManuallyExpanded && <motion.button initial={{
              opacity: 0,
              y: 10
            }} animate={{
              opacity: 1,
              y: 0
            }} exit={{
              opacity: 0,
              y: 10
            }} onClick={() => setIsManuallyExpanded(true)} className="absolute -top-8 left-1/2 -translate-x-1/2 bg-white/90 backdrop-blur-sm border border-gray-200 text-gray-500 rounded-t-xl shadow-sm hover:text-blue-600 transition-colors z-[60] flex items-center gap-1.5 px-4 py-1.5 text-[10px] font-medium uppercase tracking-tight" data-id="element-459">
                    <ChevronUp className="w-3.5 h-3.5" data-id="element-460" />
                    elegir un pack
                  </motion.button>}
              </AnimatePresence>

              <div className={`bg-white/90 backdrop-blur-md border border-gray-200 rounded-2xl shadow-[0_-10px_40px_rgba(0,0,0,0.15)] p-3 md:p-4 flex md:flex-row md:gap-4 pt-[5px] pb-[5px] pl-[12px] pr-[12px] gap-[0px] flex-col items-center justify-start transition-all duration-500 ${scrollDirection === 'down' && !isManuallyExpanded ? 'opacity-40 hover:opacity-100' : 'opacity-100'}`} data-id="element-461">
                {/* Offer Toggle */}
                <div className="flex bg-gray-100 p-1 rounded-xl w-full md:w-auto" data-id="element-462">
                  <button onClick={() => {
                setSelectedOffer('solo');
              }} className={`flex-1 md:flex-none px-4 py-2 rounded-lg text-xs font-bold transition-all ${selectedOffer === 'solo' ? 'bg-[#0a1a3a] text-white shadow-md' : 'text-gray-500 hover:text-gray-700'}`} data-id="element-463">
                    SOLO MANUAL
                  </button>
                  <button onClick={() => {
                setSelectedOffer('combo');
              }} className={`flex-1 md:flex-none px-4 py-2 rounded-lg text-xs font-bold transition-all flex items-center justify-center gap-1 ${selectedOffer === 'combo' ? 'bg-yellow-500 text-blue-900 shadow-md' : 'text-gray-500 hover:text-gray-700'}`} data-id="element-464">
                    COMBO PLC
                    <Star className={`w-3 h-3 ${selectedOffer === 'combo' ? 'fill-blue-900' : 'fill-gray-400'}`} data-id="element-465" />
                  </button>
                </div>

                {/* Price Display */}
                <div className="hidden md:flex flex-col items-start px-2" data-id="element-466">
                  <span className="text-[10px] text-gray-400 uppercase font-bold leading-none" data-id="element-467">
                    Total hoy
                  </span>
                  <span className="text-xl font-black text-gray-900 leading-none" data-id="element-468">
                    {selectedOffer === 'solo' ? '$19.990' : '$29.990'}
                  </span>
                </div>

                {/* CTA Button */}
                <button onClick={selectedOffer === 'solo' ? handlePurchaseClick : handleComboPurchaseClick} className="w-full md:w-auto flex-1 bg-green-600 hover:bg-green-500 text-white font-black py-3 px-6 rounded-xl shadow-lg shadow-green-600/20 transition-all flex items-center justify-center gap-2 group" data-id="element-469">
                  <span className="uppercase tracking-tight" data-id="element-470">
                    COMPRAR AHORA
                  </span>
                  <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" data-id="element-471" />
                </button>
              </div>
            </div>
          </motion.div>}
      </AnimatePresence>
    </div>;
}